import { CaretDownOutlined, CaretUpOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { EVisionVideoInputTypes, IVideoInputConfig } from "@kuika/kuika-cl-model";
import { memo, useEffect, useMemo, useState } from "react";
import { Handle, Position, useReactFlow, useStore } from "reactflow";
import KuikaDropdownMenu from "../../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { useHistory } from "../history-context";
import ExtendingConnector from "./ExtendingConnector";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import { useNodeCreation } from "./useNodeCreation";
import baseStyles from "./BaseNode.module.css";

interface VideoInputNodeData {
  label: string;
  title: string;
  config?: IVideoInputConfig;
}

interface VideoInputNodeProps {
  data: VideoInputNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  input: {
    headerBackground: "rgba(59, 130, 246, 0.1)",
    iconColor: "#3b82f6",
    handleColor: "#3b82f6"
  }
};

const VideoInputNode = ({ data, id, selected }: VideoInputNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [config, setConfig] = useState<IVideoInputConfig>(() => {
    // Default config values
    const defaults: IVideoInputConfig = {
      inputType: EVisionVideoInputTypes.FileUrl,
      videoSource: "",
      previewSource: ""
    };
    // Merge with provided config from template
    if (data.config) {
      return { ...defaults, ...data.config };
    }
    return defaults;
  });
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const { createConnectedNode } = useNodeCreation(id);
  const takeSnapshot = useHistory();

  // Check if source handle is connected - using useStore for reactivity
  const isSourceConnected = useStore(useMemo(() => (state) => state.edges.some((edge) => edge.source === id), [id]));

  const categoryStyles = NODE_CATEGORY_STYLES.input;

  const handleNodeSelect = (selectedNodeType: string) => {
    createConnectedNode(selectedNodeType);
  };

  const shouldShowConnector = !isSourceConnected || selected;
  const isCompactMode = isSourceConnected && selected;

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.videoInput.text}</div>}
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const updateConfig = (updates: Partial<IVideoInputConfig>) => {
    // Take snapshot before each config change for undo support
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }

    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Take snapshot before deleting
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  // Sync config state when node data changes from outside (e.g., undo/redo)
  useEffect(() => {
    if (data.config) {
      setConfig((prevConfig) => {
        const stringifiedPrev = JSON.stringify(prevConfig);
        const stringifiedNew = JSON.stringify(data.config);
        // Only update if actually different to avoid unnecessary re-renders
        return stringifiedPrev !== stringifiedNew ? { ...prevConfig, ...data.config } : prevConfig;
      });
    }
  }, [data.config]);

  const getInputTypeLabel = (inputType: EVisionVideoInputTypes | undefined): string => {
    switch (inputType) {
      case EVisionVideoInputTypes.Rtsp:
        return "RTSP Stream";
      case EVisionVideoInputTypes.Webrtc:
        return "WebRTC Stream";
      case EVisionVideoInputTypes.FileUrl:
        return "Video URL";
      case EVisionVideoInputTypes.FilePath: // TODO(Shayan): This should only be allowed for dev env, figure out how to ƒorbid access to this by normal users
        return "Local Video";
      default:
        return "Video Input";
    }
  };

  const renderSummary = () => {
    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>{getInputTypeLabel(config.inputType)}</span>
        {config.previewSource && (
          <img src={config.previewSource} alt="Preview" className={baseStyles.collapsedPreview} />
        )}
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <VideoCameraOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.videoInput.text}</div>}
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Input Type</label>
              <div className={baseStyles.nodrag}>
                <KuikaDropdownMenu
                  options={[
                    { key: EVisionVideoInputTypes.Rtsp, label: "RTSP Stream" },
                    { key: EVisionVideoInputTypes.Webrtc, label: "WebRTC Stream" },
                    { key: EVisionVideoInputTypes.FileUrl, label: "Video URL" },
                    { key: EVisionVideoInputTypes.FilePath, label: "Local Video" }
                  ]}
                  selected={config.inputType || EVisionVideoInputTypes.FileUrl}
                  setSelected={(key) => updateConfig({ inputType: key as EVisionVideoInputTypes })}
                  size="md"
                >
                  <div className={baseStyles.formSelect} style={{ cursor: "pointer" }}>
                    {config.inputType === EVisionVideoInputTypes.Rtsp && "RTSP Stream"}
                    {config.inputType === EVisionVideoInputTypes.Webrtc && "WebRTC Stream"}
                    {config.inputType === EVisionVideoInputTypes.FileUrl && "Video URL"}
                    {config.inputType === EVisionVideoInputTypes.FilePath && "Local Video"}
                    {!config.inputType && "Video URL"}
                  </div>
                </KuikaDropdownMenu>
              </div>
            </div>

            {(config.inputType === EVisionVideoInputTypes.Rtsp ||
              config.inputType === EVisionVideoInputTypes.Webrtc ||
              config.inputType === EVisionVideoInputTypes.FileUrl ||
              config.inputType === EVisionVideoInputTypes.FilePath) && (
              <div className={baseStyles.formGroup}>
                <label className={baseStyles.formLabel}>
                  {config.inputType === EVisionVideoInputTypes.Rtsp && "RTSP URL"}
                  {config.inputType === EVisionVideoInputTypes.Webrtc && "WebRTC URL"}
                  {config.inputType === EVisionVideoInputTypes.FileUrl && "Video URL"}
                  {config.inputType === EVisionVideoInputTypes.FilePath && "Local File Path"}
                </label>
                <input
                  type="text"
                  className={`${baseStyles.formInput} ${baseStyles.nodrag}`}
                  value={config.videoSource || ""}
                  onChange={(e) => updateConfig({ videoSource: e.target.value })}
                  placeholder={
                    config.inputType === EVisionVideoInputTypes.Rtsp
                      ? "rtsp://..."
                      : config.inputType === EVisionVideoInputTypes.Webrtc
                        ? "webrtc://..."
                        : config.inputType === EVisionVideoInputTypes.FileUrl
                          ? "https://..."
                          : "/path/to/video.mp4"
                  }
                />
              </div>
            )}

            {config.previewSource && (
              <img src={config.previewSource} alt="Preview" className={baseStyles.previewImage} />
            )}
          </div>
        </div>
      )}

      {/* Input handle for connection from Start node */}
      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />

      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />

      {/* Show extending connector if source handle is not connected OR node is selected */}
      {shouldShowConnector && (
        <ExtendingConnector nodeId={id} nodeType="videoInput" onNodeSelect={handleNodeSelect} compact={isCompactMode} />
      )}
    </div>
  );
};

export default memo(VideoInputNode);
